
import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableFooter,
    TableRow,
    TablePagination,
    TextField,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Paper,
    Select,
    FormControl,
    InputLabel,
    Stack,
    LinearProgress
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import SortIcon from "@mui/icons-material/Sort";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import MonitorIcon from '@mui/icons-material/Monitor';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {useTheme} from '@mui/material/styles';
import {LayoutCombinedRelatedListColumnModel,
        LayoutCombinedRelatedListMappingModel,
        LayoutCombinedRelatedListModel,
        RecordModel,
        ObjectModel} from '../../models/models';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {DisplayFieldComponent} from "./DisplayField";
import { ActionComponent } from "../../components/actions-r/Action";
import { RecordCreateDialogComponent } from "../recordCreateDialog/RecordCreateDialog";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {selectIsFetching} from "../../screens/object/combinedRelatedRecordsSlice";
import {useSelector} from "react-redux";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName, getRecordUuid } from "../../utils/helperRouter";
import {SortDirectionType} from '../../models/modelsUserConfiguration';

export const CombinedRelatedListComponent: React.FC<{
  layoutCombinedRelatedList: LayoutCombinedRelatedListModel,
  columns: LayoutCombinedRelatedListColumnModel[],
  records: RecordModel[],
  loadMoreRecords: (layoutCombinedRelatedListUuid: LayoutCombinedRelatedListModel) => void,
  selectRecord: (recordUuid: string, referencedObject: ObjectModel) => void
}> = ({
  layoutCombinedRelatedList,
  columns,
  records,
  loadMoreRecords,
  selectRecord
}) => {

  const columnAlignment = 'left'

  const styles = ContainerClasses();

  let {appName, objectName, recordUuid} = useParams();

  const [paginationPage, setPaginationPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(30);

  const [searchText, setSearchText] = useState<string>("");

  const [sortConfig, setSortConfig] = useState<{columnUuid: string, sortDirection: SortDirectionType}>({columnUuid: '', sortDirection: 'asc'});

  const isFetching = useSelector(selectIsFetching);

  useEffect(() => {

    const column = columns.find((thisColumn) => thisColumn.SortColumn_sys === true);
    if(column && column.SortColumn_sys === true){
      setSortConfig({columnUuid: column.Uuid_sys,
                     sortDirection: column.SortDirection_sys})
    }

  }, [records.length, columns.length]); // eslint-disable-line react-hooks/exhaustive-deps


  const mutableRecords = useMemo(() => {
    /* define sort function */
    const sortFunction = (recordA: RecordModel,
                          recordB: RecordModel,
                          columnFieldMappings: LayoutCombinedRelatedListMappingModel[],
                          sortDirection: SortDirectionType) => {

      const thisMappingA = columnFieldMappings.find((thisMapping) => thisMapping.ReferencedLayoutCombinedRelatedListItem_sys.Uuid_sys === recordA.parentLayoutCombinedRelatedListItemUuid)
      const thisMappingB = columnFieldMappings.find((thisMapping) => thisMapping.ReferencedLayoutCombinedRelatedListItem_sys.Uuid_sys === recordB.parentLayoutCombinedRelatedListItemUuid)
      if(!thisMappingA) return 1
      if(!thisMappingB) return 1

      const fieldApiNameA = thisMappingA.ReferencedField_sys.ApiName_sys;
      const fieldApiNameB = thisMappingB.ReferencedField_sys.ApiName_sys;

      const fieldA = recordA.fields[fieldApiNameA]
      const fieldB = recordB.fields[fieldApiNameB]

      let sortIndex = 1

      if(sortDirection === 'asc'){
        fieldA?.fieldValue > fieldB?.fieldValue ? sortIndex=1 : sortIndex=-1
      }
      if(sortDirection === 'desc'){
        fieldA?.fieldValue > fieldB?.fieldValue ? sortIndex=-1 : sortIndex=1
      }

      return sortIndex
    }

    /* handle search */
    const filterTypes = ["string", "number"]
    const thisSearchText = searchText.toLowerCase();

    const filteredRecords = records.filter((thisRecord) => {

      const keys = Object.keys(thisRecord.fields);
      const fieldValuesFound = keys.filter((thisKey) => {
        const thisField = thisRecord.fields[thisKey];
        if(filterTypes.includes(typeof(thisField.fieldValue)) &&
           thisField.fieldValue.toString().toLowerCase().includes(thisSearchText)){
             return 1
           }
         })
      if(fieldValuesFound.length > 0){
        return thisRecord
      }
    })

    /*handle sort */
    const column = columns.find((thisColumn) => thisColumn.Uuid_sys === sortConfig.columnUuid);
    //nothing to sort if the column is not defined
    //or if the column is not sortable
    if(!column) return filteredRecords
    if(!column.SortColumn_sys) return filteredRecords

    const columnFieldMappings = column.mappings

    //now, sort everything using this column and corresponding mappings
    filteredRecords.sort((a,b) => sortFunction(a,b,columnFieldMappings,sortConfig.sortDirection));

    return filteredRecords;
  }, [records, searchText, sortConfig])


  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
  }

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(event.target.value);
    setPaginationPage(0);
    return
  }

  const handleChangePage = (event: any, newPage: number) => {
    setPaginationPage(newPage);
  }

  const searchRecords = (searchText: string) => {
    setSearchText(searchText);
  }

  const clearSearch = () => {
    setSearchText("");
  }

  const handleSortByChange = (columnUuid: string) => {
    const column = columns.find((thisColumn) => thisColumn.Uuid_sys === columnUuid);
    if(!column) return
    if(!column.SortColumn_sys === true) return
    setSortConfig({
       columnUuid: column.Uuid_sys,
       sortDirection: column.SortDirection_sys
    })
  }

  const handleSortDirectionChange = () => {
    const newSortConfig = {...sortConfig}
    if(sortConfig.sortDirection === 'asc') newSortConfig.sortDirection = 'desc'
    if(sortConfig.sortDirection === 'desc') newSortConfig.sortDirection = 'asc'
    setSortConfig(newSortConfig)
  }


  const rowElement = (record: RecordModel, index: number) => {

    const getValueFromColumn = (column: LayoutCombinedRelatedListColumnModel, record: RecordModel) => {
      //get the field mapping for which the currect records origin list item
      //matched the column's mapping list items
      const thisMapping = column.mappings.find((thisMapping) => {
        return thisMapping.ReferencedLayoutCombinedRelatedListItem_sys.Uuid_sys === record.parentLayoutCombinedRelatedListItemUuid
      })
      if(!thisMapping) return '<undefined>'
      const fieldApiName = thisMapping.ReferencedField_sys.ApiName_sys;
      const thisField = record.fields[fieldApiName]
      let thisValue = thisField.fieldValue;
      if(column.DataType_sys=='DateTimeField'){
        thisValue = formatDatetime(new Date(thisField.fieldValue), {variant: 'C'})
      } else if(column.DataType_sys=='ForeignKeyField'){
        thisValue=thisField.fieldValue?.lookup_display_value;
      } else if(column.DataType_sys=='OneToOneField'){
        thisValue=thisField.fieldValue?.lookup_display_value;
      }


      return (

        <Box
          key={`${column.Uuid_sys}-${thisMapping.Uuid_sys}`}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Typography
            key={`${column.Uuid_sys}-${thisMapping.Uuid_sys}-label`}
            variant='body1'
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              textAlign: 'left'
            }}
          >
           {thisMapping.ReferencedField_sys.Label_sys}:
          </Typography>
          <Typography
            key={`${column.Uuid_sys}-${thisMapping.Uuid_sys}-value`}
            variant='body1'
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textAlign: 'left'
            }}
          >
            {thisValue}
          </Typography>
        </Box>
      )
    }
    const headerColumns = layoutCombinedRelatedList.columns.filter((thisColumn) => thisColumn.ColumnType_sys=='header')

    const thisListItem = layoutCombinedRelatedList.lists.find((thisList) => thisList.Uuid_sys === record.parentLayoutCombinedRelatedListItemUuid)

   const appApiName = getAppName(appName)
   const link_to = `/${appApiName}/${record.objectApiName}/${record.fields['Uuid_sys'].fieldValue}`;

    return (<Box sx={{width: '100%',
                      borderRadius: '20px',
                      pb: 1}}
                 key={`box-${record.fields['Uuid_sys'].fieldValue}`}
            >
              <Accordion
                 key={`accordion-${record.fields['Uuid_sys'].fieldValue}`}
                defaultExpanded={false}
                sx={{
                  backgroundColor: index%2===0 ? 'grey.300' : 'grey.400'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon/>}
                >
                  {/*header elements as accordion summary */}
                  <Box>
                    <Box
                      sx={{display: 'flex',
                           flexDirection: 'row',
                           alignItems: 'center',
                           gap: 2}}
                    >
                      <Typography
                        variant='h6'
                        sx={{color: 'text.secondary',
                          fontWeight: 600,
                          minWidth: '20%',
                          textAlign: 'left'}}
                      >
                        {thisListItem?.Label_sys}
                      </Typography>
                      <Link to={link_to} onClick={handleLinkClick}>
                        Open
                      </Link>
                    </Box>
                    {headerColumns.map((thisColumn) => getValueFromColumn(thisColumn, record))}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {record.informationType === 'detail' &&
                    thisListItem?.sections.map((thisSection) => {
                    return (
                      <Grid container spacing={2}
                        key={`${thisSection.Uuid_sys}`}
                      >
                        {thisSection.fields.map((layoutField) => {
                            return (
                              <Grid item xs={12} md={6} key={layoutField.Uuid_sys}>
                                <DisplayFieldComponent
                                  layoutField = {layoutField}
                                  record = {record}
                                />
                              </Grid>
                            )
                        })}
                      </Grid>
                    )
                  })}
                  {record.informationType === 'basic' &&
                    <Box>
                      You don't have access to this record
                    </Box>
                  }
                </AccordionDetails>
              </Accordion>
            </Box>
          )
  }



  return (

    <Box sx={{p: 2}}>

      {isFetching && <LinearProgress color="primary" />}

       <Box sx={styles.containerRowB}>
          <Box>
             <MonitorIcon />
            <Typography>
              {layoutCombinedRelatedList.Label_sys}
            </Typography>
          </Box>
       </Box>

{/*
       <Box sx={{flex: 1,
                 display: "flex",
                 flexDirection: "row",
                 justifyContent: "flex-end",
                 alignItems: "center",
                 pb: 2}}
        >
          <Button
            color="primary"
            variant="contained"
            onClick={() => {loadMoreRecords(layoutCombinedRelatedList)}}
          >
            Load Records
          </Button>
        </Box>
*/}

       <Box sx={{flex: 1,
                 display: "flex",
                 flexDirection: "row",
                 justifyContent: "flex-end",
                 alignItems: "center",
                 pb: 2,
                 gap: 2}}
        >
          <FormControl
            variant="filled"
            sx={{width: '25%'}}
          >
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortConfig.columnUuid}
              onChange={(event) => handleSortByChange(event.target.value)}
              size="small"
              startAdornment={
                <InputAdornment position="start">
                  <IconButton
                    size="small"
                    onClick={() => handleSortDirectionChange()}
                  >
                    <SwapVertIcon fontSize="small"/>
                  </IconButton>
                </InputAdornment>
              }
            >
              {columns.filter((column) => column.SortColumn_sys===true)
                      .map((column) => <MenuItem key={column.Uuid_sys} value={column.Uuid_sys}>{column.Label_sys}</MenuItem>)}
            </Select>
          </FormControl>

          <SearchFieldComponent
            searchText={searchText}
            setSearchText={searchRecords}
            clearSearchText={clearSearch}
          />
        </Box>


        <Box>
         {
           mutableRecords.map((thisRecord, index) => rowElement(thisRecord, index))
         }

        </Box>


    </Box>
  );

};
